"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  AlertCircle,
  User,
  Mail,
  MapPin,
  MessageSquare,
  Loader2,
  Ban,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BookingStep } from "@/types/booking";
import { useState, useEffect, useCallback } from "react";
import {
  useLazySearchClient,
  useLazyValidatePhoneNumber,
} from "@/lib/apollo/hooks/booking-edge";
import SearchableCountrySelect from "@/components/ui/SearchableCountrySelect";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useAvailabilityPolling } from "@/hooks/useAvailabilityPolling";
import { useSlotReservationTimer } from "@/hooks/useSlotReservationTimer";
import { SlotCountdownTimer } from "@/components/ui/SlotCountdownTimer";
import { formatDuration, formatPrice, getEffectiveTotalDurationMinutes } from "@/lib/utils";
import CartSummary from "../CartSummary";
import { useWorkflowBeforeBooking } from "@/lib/api/useWorkflowForms";
import WorkflowFormModal from "../WorkflowFormModal";
import { formatScheduledDate, formatScheduledTime } from "@/lib/salon-time";

export default function PhoneForm() {
  useScrollToTop();
  const {
    selectedSalon,
    selectedServices,
    selectedStaff,
    appointmentDateTime,
    businessInfo,
    serviceClient,
    setServiceClient,
    handleNextStep,
    addToast,
    locationType,
    setLocationType,
    // Workflow form
    setWorkflowForm,
    setShowWorkflowFormModal,
    setWorkflowFormSubmissionId,
    showWorkflowFormModal,
    selectedDate,
    selectedTime,
    clearSelectedDateTime,
    slotSelectionTime,
  } = useBooking();

  // Workflow form API hook
  const { triggerBeforeBooking, loading: workflowFormLoading } =
    useWorkflowBeforeBooking();

  const [phone, setPhone] = useState(serviceClient?.phone || "");
  const [countryCode, setCountryCode] = useState(
    serviceClient?.countryCode || selectedSalon?.countryCode || "NG"
  );
  const [callingCode, setCallingCode] = useState(
    serviceClient?.callingCode || selectedSalon?.callingCode || "+234"
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);
  const [phoneValidation, setPhoneValidation] = useState<{
    isValid: boolean | null;
    formatValid: boolean | null;
    numberValid: boolean | null;
    loading: boolean;
  }>({
    isValid: null,
    formatValid: null,
    numberValid: null,
    loading: false,
  });
  const [isBlacklisted, setIsBlacklisted] = useState(false);

  // Form data for personal information
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    address: "",
    note: "",
    locationType: "",
  });

  const [formErrors, setFormErrors] = useState<any>({});

  // Email confirmation state
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);

  // GraphQL query for searching client
  const [searchClientQuery] = useLazySearchClient();

  // GraphQL query for validating phone number
  const [validatePhoneNumberQuery] = useLazyValidatePhoneNumber();

  // Callback when slot becomes unavailable (detected by polling)
  const handleSlotUnavailable = useCallback(() => {
    // Clear the selected time in context
    clearSelectedDateTime();
    // Show toast notification
    addToast({
      message: "Sorry, this time slot is no longer available. Please select another time.",
      variant: "warning",
    });
    // Navigate back to TimeAndDate step
    handleNextStep(BookingStep.TIME_AND_DATE);
  }, [clearSelectedDateTime, addToast, handleNextStep]);

  // Callback when slot reservation timer expires
  const handleTimerExpire = useCallback(() => {
    // Clear the selected time in context
    clearSelectedDateTime();
    // Show toast notification
    addToast({
      message: "Your slot reservation has expired. Please select a new time.",
      variant: "warning",
    });
    // Navigate back to TimeAndDate step
    handleNextStep(BookingStep.TIME_AND_DATE);
  }, [clearSelectedDateTime, addToast, handleNextStep]);

  // Availability polling hook - polls every 60 seconds while user is filling in details
  // This helps detect if another user booked the slot (race condition prevention)
  useAvailabilityPolling({
    salonId: selectedSalon?.id || null,
    services: selectedServices,
    staffId: selectedStaff?.id || null,
    selectedDate: selectedDate,
    selectedTime: selectedTime,
    enabled: !!selectedTime && !!selectedDate, // Only poll when a slot is selected
    pollingInterval: 60000, // 60 seconds
    onSlotUnavailable: handleSlotUnavailable,
  });

  // Slot reservation timer - 10 minute countdown from when user selected the slot
  const { formattedTime, percentRemaining } = useSlotReservationTimer({
    durationMs: 10 * 60 * 1000, // 10 minutes
    onExpire: handleTimerExpire,
    enabled: !!slotSelectionTime,
    startTime: slotSelectionTime,
  });

  // Update form data when serviceClient changes
  useEffect(() => {
    if (serviceClient) {
      setFormData({
        fullName: `${serviceClient.firstName || ""} ${
          serviceClient.lastName || ""
        }`.trim(),
        email: serviceClient.email || "",
        address: serviceClient.address || "",
        note: serviceClient.note || "",
        locationType: serviceClient.locationType || "",
      });
      setShowPersonalInfo(true);
    } else {
      setFormData({
        fullName: "",
        email: "",
        address: "",
        note: "",
        locationType: "",
      });
      setShowPersonalInfo(false);
    }
  }, [serviceClient]);

  // Set default location type to salon when component mounts, based on salon location type
  useEffect(() => {
    if (
      selectedSalon?.locationType === "flexible" ||
      selectedSalon?.locationType === "home"
    ) {
      // Default to salon for flexible or home appointment salons
      setLocationType("salon");
    }
    if (selectedSalon?.locationType === "home") {
      setLocationType("home");
    }
  }, [selectedSalon, setLocationType]);

  // Auto-search when phone number becomes valid
  useEffect(() => {
    const cleanPhone = phone.replace(/[^0-9]/g, "");
    if (cleanPhone.length >= 10 && selectedSalon?.id && !searchLoading) {
      performSearch(cleanPhone);
    }
  }, [phone, selectedSalon?.id]);

  // Debounced phone validation when phone number has 4 or more digits
  useEffect(() => {
    const cleanPhone = phone.replace(/[^0-9]/g, "");

    if (cleanPhone.length >= 4) {
      // Clear previous validation state
      setPhoneValidation((prev) => ({ ...prev, loading: true }));

      // Set up debounced validation
      const timeoutId = setTimeout(async () => {
        try {
          const { data } = await validatePhoneNumberQuery({
            countryCode: countryCode,
            phoneNumber: cleanPhone,
          });

          const validationResult = data?.validatePhoneNumber;

          setPhoneValidation({
            isValid: validationResult?.numberValid,
            formatValid: validationResult?.numberValid, // Since we only have numberValid now
            numberValid: validationResult?.numberValid,
            loading: false,
          });

          // If number is valid, trigger client search
          if (
            validationResult?.numberValid &&
            selectedSalon?.id &&
            !searchLoading
          ) {
            performSearch(cleanPhone);
          }
        } catch (error) {
          console.error("Phone validation error:", error);
          setPhoneValidation((prev) => ({ ...prev, loading: false }));
        }
      }, 500); // 500ms debounce

      return () => clearTimeout(timeoutId);
    } else {
      // Reset validation state when phone is too short
      setPhoneValidation({
        isValid: null,
        formatValid: null,
        numberValid: null,
        loading: false,
      });
    }
  }, [phone, countryCode, validatePhoneNumberQuery]);

  const performSearch = async (cleanPhone: string) => {
    if (!selectedSalon?.id) return;

    setSearchLoading(true);
    setError("");

    try {
      const { data } = await searchClientQuery({
        salonId: selectedSalon.id,
        phone: cleanPhone,
        countryCode: countryCode,
      });

      const existingClient = data?.clientSearch;
      if (existingClient) {
        if (existingClient.blacklisted) {
          setIsBlacklisted(true);
          setServiceClient(null);
        } else {
          setIsBlacklisted(false);
          setServiceClient({
            id: existingClient.id,
            fullName: `${existingClient.firstName || ""} ${
              existingClient.lastName || ""
            }`.trim(),
            firstName: existingClient.firstName || "",
            lastName: existingClient.lastName || "",
            email: existingClient.email || "",
            originalEmail: existingClient.email || "", // Store original email
            phone: cleanPhone,
            countryCode: countryCode,
            callingCode: callingCode,
            address: existingClient.address || "",
            isExistingClient: true,
            walletBalance: existingClient.walletBalance || 0,
            membershipWalletBalance: existingClient.membershipWalletBalance || 0,
            clientMembership: existingClient.clientMembership,
          });
        }
      } else {
        setIsBlacklisted(false);
        setServiceClient({
          phone: cleanPhone,
          countryCode: countryCode,
          callingCode: callingCode,
          fullName: "",
          firstName: "",
          lastName: "",
          email: "",
          address: "",
          isExistingClient: false,
        });
        setShowPersonalInfo(true);
      }

      if (!existingClient?.blacklisted) {
        // Trigger workflow form after phone validation/client search
        await triggerWorkflowForm(cleanPhone);
      }
    } catch (error) {
      setServiceClient({
        phone: cleanPhone,
        countryCode: countryCode,
        callingCode: callingCode,
        fullName: "",
        firstName: "",
        lastName: "",
        email: "",
        address: "",
        isExistingClient: false,
      });
      setShowPersonalInfo(true);
    } finally {
      setSearchLoading(false);
    }
  };

  // Trigger workflow form before booking
  const triggerWorkflowForm = async (phone: string) => {
    if (!businessInfo?.id || selectedServices.length === 0) return;

    try {
      const serviceIds = selectedServices.map((service) => {
        // Extract original service ID if it's a variant
        if (service.variantId && service.id.includes("-")) {
          return service.id.slice(0, -(service.variantId.length + 1));
        }
        return service.id;
      });

      const formData = await triggerBeforeBooking({
        businessId: businessInfo.id,
        services: serviceIds,
        phone: phone,
        callingCode: callingCode,
      });

      if (formData && formData.fields && formData.fields.length > 0) {
        setWorkflowForm(formData);
        setWorkflowFormSubmissionId(formData.submissionId || formData.id);
        setShowWorkflowFormModal(true);
      }
    } catch (error) {
      // Silently fail - workflow forms are optional
      console.error("Error triggering workflow form:", error);
    }
  };

  const validatePhone = () => {
    const cleanPhone = phone.replace(/[^0-9]/g, "");

    if (!cleanPhone) {
      setError("Phone number is required");
      return false;
    }

    if (cleanPhone.length < 5) {
      setError("Please enter a valid phone number");
      return false;
    }

    setError("");
    return true;
  };

  // Validate personal information form
  const validatePersonalInfo = () => {
    const newErrors: any = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (locationType === "home" && !formData.address.trim()) {
      newErrors.address = "Address is required for home service";
    }

    setFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const proceedToPayment = () => {
    const cleanPhone = phone.replace(/[^0-9]/g, "");

    setServiceClient({
      ...serviceClient,
      id: serviceClient?.id,
      fullName: formData.fullName,
      firstName: formData.fullName.split(" ")[0] || "",
      lastName: formData.fullName.split(" ").slice(1).join(" ") || "",
      email: formData.email,
      phone: cleanPhone,
      countryCode: countryCode,
      callingCode: callingCode,
      address: formData.address,
      note: formData.note,
      isExistingClient: !!serviceClient?.id,
    });

    handleNextStep(BookingStep.PAYMENT);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePhone()) {
      return;
    }

    // If personal info is shown, validate it too
    if (showPersonalInfo && !validatePersonalInfo()) {
      addToast({
        message: "Please fill in all required fields",
        variant: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const cleanPhone = phone.replace(/[^0-9]/g, "");

      if (showPersonalInfo) {
        // Check if email has changed or was empty for existing clients
        const emailChanged =
          serviceClient?.isExistingClient &&
          serviceClient?.originalEmail !== formData.email;
        const emailWasEmpty =
          serviceClient?.isExistingClient &&
          !serviceClient?.originalEmail &&
          formData.email;

        if (emailChanged || emailWasEmpty) {
          // Show confirmation dialog
          setLoading(false);
          setShowEmailConfirmation(true);
          return;
        }

        // No email change, proceed directly
        proceedToPayment();
      } else {
        // Phone not yet searched or no personal info shown, perform search first
        const { data } = await searchClientQuery({
          salonId: selectedSalon!.id,
          phone: cleanPhone,
          countryCode: countryCode,
        });

        const existingClient = data?.clientSearch;

        if (existingClient?.blacklisted) {
          setIsBlacklisted(true);
          setServiceClient(null);
          setLoading(false);
          return;
        }

        if (existingClient) {
          setServiceClient({
            id: existingClient.id,
            fullName: `${existingClient.firstName || ""} ${
              existingClient.lastName || ""
            }`.trim(),
            firstName: existingClient.firstName || "",
            lastName: existingClient.lastName || "",
            email: existingClient.email || "",
            originalEmail: existingClient.email || "", // Store original email
            phone: cleanPhone,
            countryCode: countryCode,
            callingCode: callingCode,
            address: existingClient.address || "",
            isExistingClient: true,
            walletBalance: existingClient.walletBalance || 0,
            membershipWalletBalance:
              existingClient.membershipWalletBalance || 0,
            clientMembership: existingClient.clientMembership,
          });
        } else {
          setServiceClient({
            phone: cleanPhone,
            countryCode: countryCode,
            callingCode: callingCode,
            fullName: "",
            firstName: "",
            lastName: "",
            email: "",
            address: "",
            isExistingClient: false,
          });
        }

        handleNextStep(BookingStep.PAYMENT);
      }
    } catch (error) {
      addToast({
        message: "An error occurred. Please try again.",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    handleNextStep(BookingStep.TIME_AND_DATE);
  };

  const formatPhoneDisplay = (value: string) => {
    const cleanValue = value.replace(/[^0-9]/g, "");

    if (cleanValue.length <= 3) {
      return cleanValue;
    } else if (cleanValue.length <= 6) {
      return `${cleanValue.slice(0, 3)} ${cleanValue.slice(3)}`;
    } else if (cleanValue.length <= 10) {
      return `${cleanValue.slice(0, 3)} ${cleanValue.slice(
        3,
        6
      )} ${cleanValue.slice(6)}`;
    } else {
      return `${cleanValue.slice(0, 4)} ${cleanValue.slice(
        4,
        7
      )} ${cleanValue.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setPhone(value);
    setError("");

    // Clear previous search results when phone changes
    if (value.length < 10) {
      setShowPersonalInfo(false);
      setServiceClient(null);
      setIsBlacklisted(false);
    }
  };

  // Get membership details for display
  const membershipServices =
    (serviceClient as any)?.clientMembership?.membership?.services?.map(
      (s: any) => s.id
    ) || [];
  const membershipRewardPercentage =
    (serviceClient as any)?.clientMembership?.membership?.rewardPercentage || 0;
  const membershipStatus = (serviceClient as any)?.clientMembership
    ?.membershipStatus;

  // Calculate if service has membership discount
  const getServicePrice = (service: any) => {
    const basePrice = service.bundleId
      ? service.price || 0
      : service.staffPrice || service.price || 0;
    const isCoveredByMembership =
      membershipStatus === "live" &&
      membershipServices.includes(service.id) &&
      membershipRewardPercentage > 0 &&
      !service.bundleId;

    if (isCoveredByMembership) {
      const discountAmount = basePrice * (membershipRewardPercentage / 100);
      return basePrice - discountAmount;
    }

    return basePrice;
  };

  const getServiceOriginalPrice = (service: any) => {
    return service.originalPrice ?? service.staffPrice ?? service.price ?? 0;
  };

  const isServiceCoveredByMembership = (serviceId: string) => {
    return (
      membershipStatus === "live" &&
      membershipServices.includes(serviceId) &&
      membershipRewardPercentage > 0
    );
  };

  // Calculate totals
  const effectiveScheduleType =
    selectedServices.length > 0 &&
    selectedServices.every(
      (s: any) => String(s?.bundleScheduleType || "sequence").toLowerCase() === "parallel"
    )
      ? "parallel"
      : "sequence";
  const totalDuration = getEffectiveTotalDurationMinutes({
    scheduleType: effectiveScheduleType,
    services: selectedServices,
  });
  const totalOriginalPrice = selectedServices.reduce(
    (sum, service) => sum + getServiceOriginalPrice(service) * (service.quantity || 1),
    0
  );
  const totalPrice = selectedServices.reduce(
    (sum, service) => sum + getServicePrice(service) * (service.quantity || 1),
    0
  );
  const totalDiscount = totalOriginalPrice - totalPrice;

  // Format appointment date and time
  const formatAppointmentDate = () => {
    if (!appointmentDateTime) return "";
    return formatScheduledDate(appointmentDateTime);
  };

  const formatAppointmentTime = () => {
    if (!appointmentDateTime) return "";
    return formatScheduledTime(appointmentDateTime);
  };

  const businessContactNumber = selectedSalon?.phone
    ? `${selectedSalon.callingCode || ""}${selectedSalon.phone}`.trim()
    : businessInfo && "phone" in businessInfo && (businessInfo as { phone: string }).phone
      ? `${(businessInfo as { callingCode?: string }).callingCode || ""}${(businessInfo as { phone: string }).phone}`
      : null;

  return (
    <>
      {/* Workflow Form Modal */}
      <WorkflowFormModal
        onSubmit={() => {
          // Form submitted, continue with booking flow
        }}
        onSkip={() => {
          // Form skipped, continue with booking flow
        }}
      />

      {/* Email Confirmation Dialog */}
      <Dialog
        open={showEmailConfirmation}
        onOpenChange={setShowEmailConfirmation}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-amber-600" />
              Confirm Email Update
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {serviceClient?.originalEmail ? (
              <>
                <p className="text-sm text-grey-900">
                  You're about to update your email address:
                </p>
                <div className="bg-grey-50 rounded-lg p-3 space-y-3">
                  <div>
                    <span className="text-xs text-grey-600">
                      Current email:
                    </span>
                    <p className="font-medium text-grey-900">
                      {serviceClient.originalEmail}
                    </p>
                  </div>
                  <div>
                    <span className="text-xs text-grey-600">New email:</span>
                    <p className="font-medium text-green-600">
                      {formData.email}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-grey-600">
                  Are you sure you want to update your email address for future
                  bookings?
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-grey-900">
                  We'll add this email address to your account:
                </p>
                <div className="bg-grey-50 rounded-lg p-3">
                  <span className="text-xs text-grey-600">Email:</span>
                  <p className="font-medium text-green-600">{formData.email}</p>
                </div>
                <p className="text-sm text-grey-600">
                  This will be used for appointment confirmations and updates.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEmailConfirmation(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowEmailConfirmation(false);
                proceedToPayment();
              }}
              className="bg-green-300 hover:bg-green-400 text-white"
            >
              Confirm & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="min-h-screen bg-grey-5 pb-24 lg:pb-0">
        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Phone Form and Personal Info */}
            <div className="lg:col-span-2">
              <div className=" ">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Slot Reservation Timer */}
                  {slotSelectionTime && (
                    <div className="flex justify-center mb-4">
                      <SlotCountdownTimer
                        formattedTime={formattedTime}
                        percentRemaining={percentRemaining}
                      />
                    </div>
                  )}

                  {/* Instruction Text */}
                  <div className="text-center">
                    {searchLoading && (
                      <div className="flex items-center justify-center gap-2 mt-2 mb-6">
                        <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                        <span className="text-sm text-green-600">
                          Searching for your account...
                        </span>
                      </div>
                    )}
                  </div>
                  {serviceClient?.isExistingClient && (
                    <div className="bg-green-300/10  rounded-lg px-4 py-2 mb-6">
                      <p className="text-green-300 font-medium">
                        Welcome back
                        {serviceClient?.fullName
                          ? `, ${serviceClient.fullName.split(" ")[0]}`
                          : ""}
                        !
                      </p>
                      <p className="text-green-300 text-sm">
                        Please confirm your details below are still correct.
                      </p>
                    </div>
                  )}

                  {/* Phone Input */}
                  <div>
                    <label className="block text-sm font-medium text-grey-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="flex gap-2 min-w-0">
                      <SearchableCountrySelect
                        value={countryCode}
                        onChange={(code, calling) => {
                          setCountryCode(code);
                          setCallingCode(calling);
                        }}
                        className="min-w-[120px] flex-shrink-0 px-4 py-2 text-lg"
                      />
                      <div className="flex-1 min-w-0 relative">
                        <input
                          type="tel"
                          value={formatPhoneDisplay(phone)}
                          onChange={handlePhoneChange}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 text-lg ${
                            error
                              ? "border-red-500"
                              : phoneValidation.isValid === true
                              ? "border-green-500"
                              : phoneValidation.isValid === false
                              ? "border-red-500"
                              : "border-grey-100"
                          }`}
                          placeholder="801 234 5678"
                          autoFocus
                        />
                        {/* Phone validation loading indicator */}
                        {phoneValidation.loading &&
                          phone.replace(/[^0-9]/g, "").length >= 4 && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <Loader2 className="h-4 w-4 animate-spin text-grey-400" />
                            </div>
                          )}
                        {/* Phone validation success indicator */}
                        {!phoneValidation.loading &&
                          phoneValidation.isValid === true && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center">
                                <svg
                                  className="h-2.5 w-2.5 text-white"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            </div>
                          )}
                        {/* Phone validation error indicator */}
                        {!phoneValidation.loading &&
                          phoneValidation.isValid === false &&
                          phone.replace(/[^0-9]/g, "").length >= 4 && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            </div>
                          )}
                      </div>
                    </div>
                    {error && (
                      <div className="flex items-center gap-2 mt-2">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        <p className="text-red-500 text-sm">{error}</p>
                      </div>
                    )}
                    {/* Phone validation feedback */}
                    {!phoneValidation.loading &&
                      phone.replace(/[^0-9]/g, "").length >= 4 && (
                        <div className="mt-2">
                          {phoneValidation.isValid === false && (
                            <div className="flex items-center gap-2">
                              <AlertCircle className="h-3 w-3 text-red-500" />
                              <p className="text-red-500 text-sm">
                                Invalid phone number
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                  </div>

                  {/* Blacklisted message - show instead of form when client is blacklisted */}
                  {isBlacklisted && (
                    <div className="border-t border-grey-100 pt-6 mt-6">
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                        <div className="flex items-start gap-3">
                          <Ban className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-amber-800 text-sm">
                              You're unable to book an appointment online with
                              this business.
                              {businessContactNumber ? (
                                <>
                                  {" "}
                                  Please contact them on{" "}
                                  <a
                                    href={`tel:${businessContactNumber.replace(/\s/g, "")}`}
                                    className="font-medium text-amber-900 underline hover:no-underline"
                                  >
                                    {businessContactNumber}
                                  </a>
                                  .
                                </>
                              ) : (
                                " Please contact the business for more information."
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Show personal information section when client is found or phone is valid */}
                  {showPersonalInfo && !isBlacklisted && (
                    <div className="border-t border-grey-100 pt-6 mt-6">
                      {/* Personal Information Form */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-grey-900">
                          {serviceClient?.isExistingClient
                            ? "Confirm Your Information"
                            : "Personal Information"}
                        </h3>

                        <div className="mb-6">
                          <label className="block text-sm font-medium text-grey-700 mb-2">
                            Full Name *
                          </label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                            <input
                              type="text"
                              value={formData.fullName}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  fullName: e.target.value,
                                })
                              }
                              className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                formErrors.fullName
                                  ? "border-red-500"
                                  : "border-grey-100"
                              }`}
                              placeholder="John Doe"
                            />
                          </div>
                          {formErrors.fullName && (
                            <p className="text-red-500 text-xs mt-1">
                              {formErrors.fullName}
                            </p>
                          )}
                        </div>

                        <div className="mb-6">
                          <label className="block text-sm font-medium text-grey-700 mb-2">
                            Email Address *
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
                            <input
                              type="email"
                              value={formData.email}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  email: e.target.value,
                                })
                              }
                              className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                formErrors.email
                                  ? "border-red-500"
                                  : "border-grey-100"
                              }`}
                              placeholder="john.doe@example.com"
                            />
                          </div>
                          {formErrors.email && (
                            <p className="text-red-500 text-xs mt-1">
                              {formErrors.email}
                            </p>
                          )}
                        </div>

                        {/* Home Appointment Radio Button */}
                        {selectedSalon?.locationType === "flexible" ? (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-grey-700 mb-3">
                                Are you booking a home appointment?
                              </label>
                              <div className="space-y-2">
                                <label className="flex items-center gap-3 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="appointmentType"
                                    value="salon"
                                    checked={locationType === "salon"}
                                    onChange={() => setLocationType("salon")}
                                    className="w-4 h-4 text-green-300 focus:ring-green-300 accent-green-300"
                                  />
                                  <span className="text-sm text-grey-700">
                                    No, I'll visit the salon
                                  </span>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer">
                                  <input
                                    type="radio"
                                    name="appointmentType"
                                    value="home"
                                    checked={locationType === "home"}
                                    onChange={() => setLocationType("home")}
                                    className="w-4 h-4 text-green-300 focus:ring-green-300 accent-green-300"
                                  />
                                  <span className="text-sm text-grey-700">
                                    Yes, I want a home appointment
                                  </span>
                                </label>
                              </div>
                            </div>

                            {/* Address for Home Service */}
                            {locationType === "home" && (
                              <div>
                                <label className="block text-sm font-medium text-grey-700 mb-2">
                                  Service Address *
                                </label>
                                <div className="relative">
                                  <MapPin className="absolute left-3 top-3 h-4 w-4 text-grey-400" />
                                  <input
                                    value={formData.address}
                                    onChange={(e) => {
                                      setFormData({
                                        ...formData,
                                        address: e.target.value,
                                      });
                                    }}
                                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                      formErrors.address
                                        ? "border-red-500"
                                        : "border-grey-100"
                                    }`}
                                    placeholder="Enter your full address for home service"
                                  />
                                </div>
                                {formErrors.address && (
                                  <p className="text-red-500 text-xs mt-1">
                                    {formErrors.address}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        ) : null}

                        {selectedSalon?.locationType === "home" && (
                          <div>
                            <label className="block text-sm font-medium text-grey-700 mb-2">
                              Service Address *
                            </label>
                            <div className="relative">
                              <MapPin className="absolute left-3 top-3 h-4 w-4 text-grey-400" />
                              <input
                                value={formData.address}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    address: e.target.value,
                                  });
                                }}
                                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                                  formErrors.address
                                    ? "border-red-500"
                                    : "border-grey-100"
                                }`}
                                placeholder="Enter your full address for home service"
                              />
                            </div>
                            {formErrors.address && (
                              <p className="text-red-500 text-xs mt-1">
                                {formErrors.address}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Additional Notes */}
                        <div>
                          <label className="block text-sm font-medium text-grey-700 mb-2">
                            Additional Notes (Optional)
                          </label>
                          <div className="relative">
                            <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-grey-400" />
                            <textarea
                              value={formData.note}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  note: e.target.value,
                                })
                              }
                              className="w-full pl-10 pr-3 py-2 placeholder:text-sm border border-grey-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300"
                              rows={5}
                              placeholder="Any special requests or notes for your appointment..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Info Box - Only show if personal info is not shown and not blacklisted */}
                  {!showPersonalInfo && !isBlacklisted && (
                    <div className="bg-green-300/10    rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-green-300 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-green-300">
                          <p className="font-medium mb-1">
                            Why do we need your phone number?
                          </p>
                          <ul className="space-y-1 text-xs">
                            <li>• To send appointment confirmations</li>
                            <li>• To notify you of any changes</li>
                            <li>• To provide better customer service</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Continue Button - Desktop */}
                  <div className="hidden lg:block">
                    <Button
                      type="submit"
                      disabled={
                        loading ||
                        isBlacklisted ||
                        !phone ||
                        (phoneValidation.isValid === false &&
                          phone.replace(/[^0-9]/g, "").length >= 4) ||
                        (showPersonalInfo &&
                          (!formData.fullName || !formData.email))
                      }
                      className="w-full bg-green-300 hover:bg-green-400 text-white h-12 disabled:opacity-50"
                    >
                      {loading
                        ? "Processing..."
                        : showPersonalInfo
                        ? "Proceed"
                        : "Continue"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right Column - Booking Summary - Only show when personal info is visible */}
            {showPersonalInfo && (
              <CartSummary
                showDateTime={true}
                showMembershipBanner={
                  serviceClient &&
                  serviceClient?.clientMembership &&
                  serviceClient?.clientMembership?.membershipStatus === "live"
                }
                customButton={{
                  text: "Proceed",
                  onClick: (e) => handleSubmit(e),
                  disabled:
                    loading ||
                    isBlacklisted ||
                    !phone ||
                    (phoneValidation.isValid === false &&
                      phone.replace(/[^0-9]/g, "").length >= 4) ||
                    (showPersonalInfo &&
                      (!formData.fullName || !formData.email)),
                }}
              />
            )}
          </div>
        </div>

        {/* Mobile Sticky Footer */}
        <div className="fixed bottom-0 left-0 right-0 w-full bg-white border-t border-grey-100 p-4 lg:hidden">
          <div className="flex gap-3">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1 border-grey-100"
            >
              Back
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                loading ||
                isBlacklisted ||
                !phone ||
                (phoneValidation.isValid === false &&
                  phone.replace(/[^0-9]/g, "").length >= 4) ||
                (showPersonalInfo && (!formData.fullName || !formData.email))
              }
              className="flex-1 bg-green-300 hover:bg-green-400 text-white disabled:opacity-50"
            >
              {loading
                ? "Processing..."
                : showPersonalInfo
                ? "Continue to Payment"
                : "Continue"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
